import { type VoidAsyncResult, combineVoidAsyncResults, createResolvedAsyncResult } from "@/cache/AsyncResult";
import { createPVSTileService } from "@/cache/PVSTileService/PVSTileServiceFactory";
import type { TileGetter } from "@/cache/PVSTileService/TileGetters/TileGetter";
import { apiThreadPool } from "@/cache/SpatioTemporalTileCache/ApiQueryThreadPool";
import { BinaryTile } from "@/cache/SpatioTemporalTileCache/BinaryTileCache";
import type { TilesByLocation } from "@/cache/SpatioTemporalTileCache/PotentiallyVisibleTileSet";
import type { TileDataDescription } from "@/cache/SpatioTemporalTileCache/TileDataDescription";
import { TileGeometry } from "@/cache/SpatioTemporalTileCache/TileGeometry";
import { prepareBinaryCrop } from "@/cache/SpatioTemporalTileCache/binaryCropUtils";
import type { AreaRequest } from "@/layers/AreaRequest";
import type { ScenePublicApi } from "@/layers/Compositor";
import { LayerBase, type PropsChecker } from "@/layers/LayerBase";
import { RequestedTileGridOverlay } from "@/layers/debug/RequestedTileGridOverlay";
import {
  getCurrentTileLayerTransitionRuntime,
  shouldDisableTileLayerTransitions,
  shouldRenderRetainedFallbackTiles,
  shouldRequestLowQualityBackingTiles,
  syncTileLayerTransitionState,
} from "@/layers/png/tileLayerTransitionPolicy";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import { isWmtsTileBoundariesDebugEnabled } from "@/map/mapDebugOptions";
import { recordLayerFetchMs } from "@/otel/metrics";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { formatByteSize } from "@/utility/bytesize";
import { safeFmt } from "@/utility/safeFmt";
import { getModelBoundingBox } from "@/weather-parameters/lookup";
import { BinaryColormapState, type FixedBinaryColormap } from "./BinaryColormapState";
import { BinaryDebugRenderer, computeMinMax } from "./BinaryDebugRenderer";
import { BinaryDrapedTileRenderer } from "./BinaryDrapedTileRenderer";
import { BinaryMainpassRenderer } from "./BinaryMainpassRenderer";
import {
  BinaryTilePrepassRenderer,
  FULL_TEXTURE_COORDS,
  calculateTextureCoordinates,
} from "./BinaryTilePrepassRenderer";

import {
  type CoordinateSystem,
  type GridRequest,
  GridSamplingStrategyKind,
  MeteomaticsApiUrl,
  type ParameterUnit,
} from "@mm/api.meteomatics.com";
import type { GuiTimeZone, WmsLayer } from "@mm/metx-workbench.meteomatics.com";
import type { DateTime } from "luxon";
import type * as Mapbox from "mapbox-gl";
import type { ProjectionSpecification } from "mapbox-gl";
import type { WmtsDebugTile } from "../WMTSLayerImpl/wmtsDebugGrid";

type TilesByZoomLevel = Map<number, TilesByLocation<BinaryTile>>;

type RequestParams = {
  model: string;
  parameter_unit: string;
  ens_select: string;
  init_date?: string;
  api_url_suffix?: string;
};

type BinaryTileGetter = TileGetter<
  CoordinateSystem.EPSG3857,
  BinaryTile,
  RequestParams,
  { tileGeometry: TileGeometry; request: DeclutteredGridRequest<CoordinateSystem.EPSG3857> }
>;

type AnyBinaryTileGetter = TileGetter<CoordinateSystem.EPSG3857, BinaryTile, any, any>;

type PvsLike = {
  get?: () => TilesByZoomLevel;
  isCovering: () => boolean;
  props?: { isExact?: boolean };
  toVoidAsyncResult?: () => VoidAsyncResult;
};

type DeclutteredGridRequest<C extends CoordinateSystem> = GridRequest<C> & {
  decluttered?: boolean;
};

const BACKING_TILE_MAX_ZOOM = 3;
const TIME_STEP_FADE_MS = 250;

export function isTileOutOfModelBounds(
  tileGeometry: TileGeometry,
  modelBounds: ReturnType<typeof getModelBoundingBox>,
): boolean {
  const geometryEpsg3857 = tileGeometry.toEpsg3857();
  const intersection = modelBounds.contains(geometryEpsg3857.project(modelBounds.crs));
  return intersection.kind === "OutOfBounds";
}

// Binary colormap renderer (client-side rendering).
// Implemented as a Mapbox CustomLayerInterface to keep binary tiles isolated from PNG/WMS/WMTS paths.
// Technique: Standard-Matrix on flat projections (u_mercatorToGl from Mapbox). On globe, uses texture draping via renderToTile.
export type BinaryColormapLayerOptions = {
  renderingMode?: "2d" | "3d";
  tileGetterFactory?: (emptyTile: BinaryTile, failureTile: BinaryTile) => AnyBinaryTileGetter;
  fixedColormap?: FixedBinaryColormap;
  onUnsupportedHardware?: (reason: string) => void;
};

export class BinaryColormapLayerImpl extends LayerBase<WmsLayer> implements Mapbox.CustomLayerInterface {
  id: string;
  readonly type = "custom" as const;
  readonly renderingMode: "2d" | "3d";

  private gl?: WebGL2RenderingContext;
  // Binary rendering can be disabled or unsupported; keep PNG/WMS/WMTS fallback active.
  private binaryEnabled = true;
  private binarySupported = true;
  private binaryUnsupportedReason?: string;
  private warnedBinaryDisabled = false;

  // Dev-only debug logging gate.
  private debugLoggingEnabled = false;

  // Tile service + last computed PVS (instant view).
  private tileService?: ReturnType<typeof createPVSTileService<CoordinateSystem.EPSG3857, AnyBinaryTileGetter>>;
  private lastPvs?: PvsLike;
  private fallbackPvs?: PvsLike;
  private backingPvsByZoom: PvsLike[] = [];
  private drapePvs?: PvsLike;
  private drapePvsFallback?: PvsLike;

  // Renderer/state helpers keep each stage focused and testable.
  private debugRenderer = new BinaryDebugRenderer();
  private prepassRenderer = new BinaryTilePrepassRenderer();
  private mainpassRenderer = new BinaryMainpassRenderer();
  private drapedRenderer = new BinaryDrapedTileRenderer();
  private colormapState: BinaryColormapState;

  private geoMinMax?: { min: number; max: number };

  private projectionName?: string;
  private useDraping = false;
  private drapeDirty = false;
  private lastDrapeKey?: string;
  private seenRenderToTileThisFrame = false;
  private drapeStats = { seen: false, tiles: 0, missing: 0, parent: 0 };
  private lastShouldRerenderTiles?: boolean;
  private lastRerenderLogAt = 0;
  private lastMissingLogAt = 0;
  private idealTileFadeStartMs = new Map<string, number>();
  private settledIdealTileKeys = new Set<string>();
  private lastObservedTimeKey: string | null = null;
  private lastObservedViewKey: string | null = null;
  private lastObservedZoomValue: number | null = null;
  private pendingTimeTransition = false;
  private pendingZoomDirection: "in" | "out" | null = null;
  private readonly requestedTileGridOverlay: RequestedTileGridOverlay;

  // Mapbox can pass wrapped tile x; tell it we want canonical ids.
  readonly wrapTileId = true;

  constructor(
    uid: number,
    props: WmsLayer,
    scene: ScenePublicApi,
    timezone: GuiTimeZone,
    private readonly options?: BinaryColormapLayerOptions,
  ) {
    super(uid, props, scene, timezone);
    this.colormapState = new BinaryColormapState(this.options?.fixedColormap);
    this.id = this.humanReadableId();
    this.requestedTileGridOverlay = new RequestedTileGridOverlay(
      `${this.id}.bin.debug-grid.source`,
      `${this.id}.bin.debug-grid.layer`,
    );
    this.renderingMode = this.options?.renderingMode ?? "2d";
    const env = (globalThis as any)?.process?.env?.NODE_ENV;
    this.debugLoggingEnabled = env !== "production";

    // Optional feature flag from custom_options.
    type BinaryCustomOptions = {
      binaryTiles?: unknown;
      binary_tiles?: unknown;
      binary?: unknown;
    };
    const co: unknown = (this.props as any).custom_options;
    const coObj = co && typeof co === "object" ? (co as BinaryCustomOptions) : undefined;
    if (coObj && ("binaryTiles" in coObj || "binary_tiles" in coObj || "binary" in coObj)) {
      this.binaryEnabled = !!(coObj.binaryTiles ?? coObj.binary_tiles ?? coObj.binary);
    }

    this.scene.getMapboxMap().addLayer(this as unknown as Mapbox.CustomLayerInterface);
  }

  get mapboxIndex(): string {
    return this.id;
  }

  humanReadableId(): string {
    return safeFmt`metx.BIN.${this.props.model}:${this.props.parameter_unit}${
      this.props.ens_select ? `-${this.props.ens_select}` : ""
    }@${this.props.color_map}#${this.uid}`;
  }

  private shouldRenderBinary(): boolean {
    if (!this.binaryEnabled) {
      return false;
    }
    return this.binarySupported;
  }

  private warnBinaryDisabledOnce() {
    if (this.warnedBinaryDisabled || !this.debugLoggingEnabled) {
      return;
    }
    this.warnedBinaryDisabled = true;
    const reason = this.binarySupported ? "disabled by feature flag" : this.binaryUnsupportedReason || "unsupported";
    // eslint-disable-next-line no-console
    console.warn(
      `[BinaryColormap] binary rendering inactive (${reason}). Keeping layer transparent; caller should fall back to PNG/WMS/WMTS.`,
    );
  }

  checker(): PropsChecker<WmsLayer, LayerBase<WmsLayer>> {
    const checkLayerProp = (name: any, onChanged?: () => void) => (prev: any, curr: any) => {
      const changed = prev[name] !== curr[name];
      if (changed) {
        this.setLayerProps({ [name]: curr[name] });
        onChanged?.();
        // Ensure prerender()/render() runs immediately; otherwise changes may only apply after the next interaction.
        this.scene.repaint();
      }
      return changed;
    };

    const resetColormapState = () => {
      // Force next prerender() to re-request the LUT and clear stale mappings.
      this.colormapState.reset();
    };

    return {
      model: checkLayerProp("model", resetColormapState),
      opacity: checkLayerProp("opacity"),
      parameter_unit: checkLayerProp("parameter_unit", resetColormapState),
      show: checkLayerProp("show"),
      calibrated: checkLayerProp("calibrated", resetColormapState),
      vertical_interpolation: checkLayerProp("vertical_interpolation", resetColormapState),
      color_map: checkLayerProp("color_map" /* keep lastLoadedColormap to avoid flash */),
      legend_visible: checkLayerProp("legend_visible"),
      ens_select: checkLayerProp("ens_select", resetColormapState),
      custom_options: checkLayerProp("custom_options"),
    };
  }

  // Mapbox lifecycle
  onAdd(_map: Mapbox.Map, gl: WebGL2RenderingContext): void {
    this.gl = gl;
    // Avoid alignment issues when uploading float textures.
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    // Binary tiles require float textures. If unavailable, keep the layer transparent.
    const hasFloatSupport = !!gl.getExtension("EXT_color_buffer_float");
    if (!hasFloatSupport) {
      this.binarySupported = false;
      this.binaryUnsupportedReason = "missing float texture support";
      this.options?.onUnsupportedHardware?.(this.binaryUnsupportedReason);
      this.warnBinaryDisabledOnce();
      return;
    }

    if (!this.binaryEnabled) {
      this.warnBinaryDisabledOnce();
      return;
    }

    const sharedEmptyTile = new BinaryTile({
      width: 2,
      height: 2,
      payload: new Float32Array(4),
      estimatedByteSize: 16,
      interpolationMode: 0 as any,
    });
    const sharedFailureTile = new BinaryTile({
      width: 2,
      height: 2,
      payload: new Float32Array(4),
      estimatedByteSize: 16,
      interpolationMode: 0 as any,
    });

    const defaultTileGetter: BinaryTileGetter = {
      createEmptyTile: () => sharedEmptyTile,
      createFailureTile: () => sharedFailureTile,
      createTileApiRequest: (tileDesc: TileDataDescription<RequestParams>, tilePixelDimension: number) => {
        const geometry = tileDesc.geometry;
        const area = geometry.toEpsg3857();

        // Some sources/models have tighter spatial coverage / bbox limits.
        // When we wire `source` for binary tiles, use a source/model specific bbox here to avoid
        // requesting outside coverage (which can lead to reserved values / empty tiles).
        const request: DeclutteredGridRequest<CoordinateSystem.EPSG3857> = {
          boundingBoxLimit: getModelBoundingBox(this.props.model),
          model: tileDesc.requestParams.model,
          parameters: [tileDesc.requestParams.parameter_unit as unknown as ParameterUnit],
          ensSelect: tileDesc.requestParams.ens_select || undefined,
          initDate: tileDesc.requestParams.init_date,
          datetime: tileDesc.datetime,
          area,
          // IMPORTANT: required by MeteomaticsApiUrl.forGrid -> Area.applySamplingStrategy
          // If omitted, runtime will crash when formatting the URL.
          sampling: { kind: GridSamplingStrategyKind.Area },
          width: tilePixelDimension,
          height: tilePixelDimension,
          calibrated: this.props.calibrated ?? undefined,
          vertical_interpolation: this.props.vertical_interpolation ?? undefined,
          apiUrlSuffix: tileDesc.requestParams.api_url_suffix,
          decluttered: true,
        };

        return { tileGeometry: geometry, request };
      },
      requestTile: (tileApiRequest, requestRetryState) => {
        const modelBounds = getModelBoundingBox(tileApiRequest.request.model);
        if (isTileOutOfModelBounds(tileApiRequest.tileGeometry, modelBounds)) {
          return Promise.resolve(sharedEmptyTile);
        }

        // Delegates to the worker pool.
        // ApiQueryThreadPool returns a BinaryTile which wraps the Float32 tile payload.
        const fetchStarted = performance.now();
        return apiThreadPool
          .getBinaryTile(tileApiRequest.tileGeometry, tileApiRequest.request, requestRetryState)
          .then((tile: BinaryTile) => {
            recordLayerFetchMs({
              durationMs: performance.now() - fetchStarted,
              source: "binary_tile",
            });
            return tile;
          });
      },
      getTileCacheId: (tileApiRequest) => {
        const request = tileApiRequest.request;
        return [
          tileApiRequest.tileGeometry.toDataId(),
          request.model,
          request.parameters.join(","),
          request.ensSelect ?? "",
          request.initDate ?? "",
          request.calibrated ? "cal" : "raw",
          request.vertical_interpolation ?? "",
          request.apiUrlSuffix ?? "",
          request.decluttered ?? "",
        ].join("|");
      },
      getAbortUrls: (tileApiRequest) => {
        const cropPlan = prepareBinaryCrop(tileApiRequest.request);
        if (cropPlan.kind === "empty") {
          return [];
        }
        return [MeteomaticsApiUrl.forGrid({ format: "bin" }, cropPlan.request).toString()];
      },
    };

    const tileGetter: AnyBinaryTileGetter =
      this.options?.tileGetterFactory?.(sharedEmptyTile, sharedFailureTile) ?? defaultTileGetter;

    this.tileService = createPVSTileService<CoordinateSystem.EPSG3857, typeof tileGetter>({
      label: this.humanReadableId(),
      tileSize: 256,
      maxSize: 512 * 1024 * 1024,
      formatSize: formatByteSize,
      delayEvictionUntilSize: 800 * 1024 * 1024,
      tileGetter,
    });

    this.debugRenderer.init(gl);
    this.prepassRenderer.init(gl);
    this.mainpassRenderer.init(gl);
    this.drapedRenderer.init(gl);
  }

  // @ts-ignore mapbox passes additional args for globe projection
  prerender(
    _gl: WebGL2RenderingContext,
    _matrix: number[],
    projection?: ProjectionSpecification,
    _projectionToMercatorMatrix?: number[],
    _projectionToMercatorTransition?: number,
    _centerInMercator?: number[],
    _pixelsPerMeterRatio?: number,
  ): void {
    if (this.debugLoggingEnabled && this.drapeStats.seen) {
      // eslint-disable-next-line no-console
      console.debug(
        `[BinaryDraped] frame tiles=${this.drapeStats.tiles} missing=${this.drapeStats.missing} parentFallback=${this.drapeStats.parent}`,
      );
    }
    this.drapeStats = { seen: false, tiles: 0, missing: 0, parent: 0 };
    this.seenRenderToTileThisFrame = false;
    this.updateProjectionState(projection);
    if (!this.tileService || !this.props.show || this.props.opacity <= 0.0) {
      return;
    }

    if (!this.shouldRenderBinary()) {
      this.warnBinaryDisabledOnce();
      return;
    }

    const viewport = this.getTileViewport();

    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDisplayTimeWithOffset(),
      this.props.kind as LayerKindName,
    );
    this.syncInteractionState(dateTimeWithOffset, viewport);

    const customInitDate =
      typeof this.props.custom_options?.init_date === "string" ? this.props.custom_options.init_date.trim() : "";
    const customOptions = this.props.custom_options as { api_url_suffix?: string } | undefined;
    const customApiUrlSuffix =
      typeof customOptions?.api_url_suffix === "string" ? customOptions.api_url_suffix.trim() : "";
    const areaRequest: AreaRequest<{
      model: string;
      parameter_unit: string;
      ens_select: string;
      init_date?: string;
      api_url_suffix?: string;
    }> = {
      area: viewport.viewportArea,
      datetime: dateTimeWithOffset,
      requestParams: {
        model: this.props.model,
        parameter_unit: this.props.parameter_unit,
        ens_select: this.props.ens_select || "",
        init_date: customInitDate || undefined,
        api_url_suffix: customApiUrlSuffix || undefined,
      },
    };

    this.lastPvs = this.tileService.getTileSetInstant(areaRequest);
    this.backingPvsByZoom = this.getBackingTileSets(areaRequest, viewport.zoom);
    if (this.useDraping) {
      const drapePvs = this.getDrapingTileSet(areaRequest, viewport.viewportArea);
      this.drapePvs = drapePvs.primary;
      this.drapePvsFallback = drapePvs.fallback;
    } else {
      this.drapePvs = undefined;
      this.drapePvsFallback = undefined;
    }

    this.debugRenderer.updateDebugTextureIfNeeded(this.gl, this.scene, this.lastPvs);

    this.colormapState.updateIfNeeded(
      {
        parameter: this.props.parameter_unit,
        colorMapName: this.props.color_map,
        ensSelect: this.props.ens_select || undefined,
        calibrated: this.props.calibrated ?? undefined,
        verticalInterpolation: this.props.vertical_interpolation ?? undefined,
      },
      this.scene,
    );

    this.ensureGeoMinMaxFromAnyTile();

    const colormapInfo = this.colormapState.getRenderInfo();
    const drapeKey = [
      dateTimeWithOffset.toMillis(),
      this.props.model,
      this.props.parameter_unit,
      this.props.ens_select || "",
      customInitDate || "",
      this.props.calibrated ? "cal" : "raw",
      this.props.vertical_interpolation || "",
      this.props.color_map || "",
      this.props.opacity,
      colormapInfo.requestedId,
      viewport.zoomWithPrecision.toFixed(4),
    ].join("|");
    if (this.lastDrapeKey !== drapeKey) {
      this.lastDrapeKey = drapeKey;
      this.drapeDirty = true;
    }
    if (this.useDraping && this.projectionName !== "globe") {
      this.useDraping = false;
    }
    if (this.useDraping && this.scene.getMapboxMap().isMoving?.()) {
      this.drapeDirty = true;
    }

    // Keep requesting repaint while tiles are still missing.
    if (this.lastPvs && !this.lastPvs.isCovering()) {
      this.scene.repaint();
      this.drapeDirty = true;
    }
    // Also repaint while the colormap LUT is still loading. Otherwise the layer can stay
    // in fallback mode until the next user interaction triggers a new frame.
    if (this.colormapState.isPending()) {
      this.scene.repaint();
      this.drapeDirty = true;
    }
  }
  private ensureGeoMinMaxFromAnyTile() {
    if (this.geoMinMax) {
      return;
    }

    const tilesByZoomLevel = this.getRenderableTilesByZoomLevel();
    if (!tilesByZoomLevel || tilesByZoomLevel.size === 0) {
      return;
    }

    const zooms = Array.from(tilesByZoomLevel.keys()).sort((a, b) => b - a);
    for (const z of zooms) {
      const sublayerTiles = tilesByZoomLevel.get(z);
      if (!sublayerTiles) {
        continue;
      }
      if (!sublayerTiles || typeof sublayerTiles !== "object") {
        continue;
      }
      for (const [, positioned] of Object.entries(sublayerTiles as Record<string, unknown>)) {
        const pt = positioned as any;
        if (!pt || pt.isFailedTile) {
          continue;
        }

        const tileData: any = pt.tileData;
        const payload: Float32Array | undefined = tileData?.payload ?? tileData?.data?.payload;
        if (!payload) {
          continue;
        }

        this.geoMinMax = computeMinMax(payload);
        return;
      }
    }
  }

  render(
    gl: WebGL2RenderingContext,
    matrix: number[],
    projection?: ProjectionSpecification,
    _projectionToMercatorMatrix?: number[],
    _projectionToMercatorTransition?: number,
    _centerInMercator?: number[],
    _pixelsPerMeterRatio?: number,
  ): void {
    this.updateProjectionState(projection);
    if (this.useDraping && this.projectionName === "globe") {
      // Texture-Sampling / Surface Draping: tiles are rendered by Mapbox renderToTile on globe.
      if (this.seenRenderToTileThisFrame && this.lastPvs?.isCovering() && !this.colormapState.isPending()) {
        this.drapeDirty = false;
      }
      return;
    }
    if (!this.props.show || this.props.opacity <= 0.0) {
      return;
    }
    if (!this.lastPvs && !this.fallbackPvs) {
      return;
    }

    if (!this.shouldRenderBinary()) {
      this.warnBinaryDisabledOnce();
      return;
    }
    gl.disable(gl.STENCIL_TEST);
    gl.disable(gl.SCISSOR_TEST);
    gl.disable(gl.CULL_FACE);

    // Ensure prepass targets exist and match the current drawing buffer size.
    const ensure = this.prepassRenderer.ensureTargets(gl);
    if (!ensure.ok) {
      this.binarySupported = false;
      this.binaryUnsupportedReason = ensure.reason || "prepass target allocation failed";
      this.options?.onUnsupportedHardware?.(this.binaryUnsupportedReason);
      this.warnBinaryDisabledOnce();
      return;
    }

    // Draw georeferenced base + overlay tile sets into separate prepass buffers.
    const viewport = this.getTileViewport();
    const { baseTilesByZoom, overlayTilesByZoom, overlayTileOpacityByKey, hasActiveFade } =
      this.getPrepassTileSets(viewport);
    this.prepassRenderer.draw(
      gl,
      matrix,
      viewport,
      baseTilesByZoom,
      overlayTilesByZoom,
      overlayTileOpacityByKey,
      this.tileService,
    );

    const prepassTargets = this.prepassRenderer.getTargets();
    if (!prepassTargets.baseColorTexture || !prepassTargets.overlayColorTexture) {
      return;
    }

    // Composite the prepass onto the screen (fullscreen).
    this.mainpassRenderer.draw(gl, {
      prepassTexture: prepassTargets.baseColorTexture,
      overlayPrepassTexture: prepassTargets.overlayColorTexture,
      opacity: this.props.opacity,
      colorMapName: this.props.color_map,
      parameterUnit: this.props.parameter_unit,
      geoMinMax: this.geoMinMax,
      colormapInfo: this.colormapState.getRenderInfo(),
      debugLoggingEnabled: this.debugLoggingEnabled,
    });

    this.finalizePendingTimeTransition(hasActiveFade);
    this.finalizePendingZoomTransition(hasActiveFade);
    this.captureCurrentAsFallbackAfterFadesSettle(hasActiveFade);
  }

  private getRenderableTilesByZoomLevel(): TilesByZoomLevel {
    const currentTilesByZoom: TilesByZoomLevel =
      this.lastPvs?.get?.() ?? new Map<number, TilesByLocation<BinaryTile>>();
    const hasBackingTiles = this.backingPvsByZoom.length > 0;
    const fallbackPvs = this.shouldRenderFallbackTiles() ? this.fallbackPvs : undefined;
    if (
      (!fallbackPvs || fallbackPvs === this.lastPvs) &&
      !hasBackingTiles &&
      !this.drapePvs &&
      !this.drapePvsFallback
    ) {
      return currentTilesByZoom;
    }

    const fallbackTilesByZoom: TilesByZoomLevel =
      fallbackPvs?.get?.() ?? new Map<number, TilesByLocation<BinaryTile>>();
    const backingTilesByZoom = this.createBaseTilesByZoom(currentTilesByZoom);
    const drapeTilesByZoom: TilesByZoomLevel = this.drapePvs?.get?.() ?? new Map<number, TilesByLocation<BinaryTile>>();
    const drapeFallbackTilesByZoom: TilesByZoomLevel =
      this.drapePvsFallback?.get?.() ?? new Map<number, TilesByLocation<BinaryTile>>();
    if (fallbackTilesByZoom.size === 0 && backingTilesByZoom.size === 0) {
      if (drapeTilesByZoom.size === 0 && drapeFallbackTilesByZoom.size === 0) {
        return currentTilesByZoom;
      }
      const merged = new Map<number, TilesByLocation<BinaryTile>>();
      for (const [zoom, tiles] of drapeTilesByZoom.entries()) {
        merged.set(zoom, { ...tiles });
      }
      for (const [zoom, tiles] of drapeFallbackTilesByZoom.entries()) {
        const existing = merged.get(zoom) || {};
        merged.set(zoom, { ...existing, ...tiles });
      }
      for (const [zoom, tiles] of currentTilesByZoom.entries()) {
        const existing = merged.get(zoom) || {};
        merged.set(zoom, { ...existing, ...tiles });
      }
      return merged;
    }
    if (currentTilesByZoom.size === 0) {
      if (drapeTilesByZoom.size === 0 && drapeFallbackTilesByZoom.size === 0) {
        return fallbackTilesByZoom;
      }
      const merged = new Map<number, TilesByLocation<BinaryTile>>();
      for (const [zoom, tiles] of fallbackTilesByZoom.entries()) {
        merged.set(zoom, { ...tiles });
      }
      for (const [zoom, tiles] of drapeTilesByZoom.entries()) {
        const existing = merged.get(zoom) || {};
        merged.set(zoom, { ...existing, ...tiles });
      }
      for (const [zoom, tiles] of drapeFallbackTilesByZoom.entries()) {
        const existing = merged.get(zoom) || {};
        merged.set(zoom, { ...existing, ...tiles });
      }
      return merged;
    }

    const merged: TilesByZoomLevel = new Map();
    const mergeInto = (map: TilesByZoomLevel) => {
      for (const [zoom, tiles] of map.entries()) {
        const existing = merged.get(zoom) || {};
        merged.set(zoom, { ...existing, ...tiles });
      }
    };

    mergeInto(backingTilesByZoom);
    mergeInto(fallbackTilesByZoom);
    mergeInto(drapeTilesByZoom);
    mergeInto(drapeFallbackTilesByZoom);
    mergeInto(currentTilesByZoom);

    return merged;
  }

  private getPrepassTileSets(viewport: ReturnType<BinaryColormapLayerImpl["getTileViewport"]>) {
    const overlayTilesByZoom = this.lastPvs?.get?.() ?? new Map<number, TilesByLocation<BinaryTile>>();
    const { opacityByKey: overlayTileOpacityByKey, hasActiveFade } = this.getOverlayTileOpacityByKey(
      overlayTilesByZoom,
      viewport,
    );
    const baseTilesByZoom = this.createBaseTilesByZoom(overlayTilesByZoom);

    return {
      baseTilesByZoom,
      overlayTilesByZoom,
      overlayTileOpacityByKey,
      hasActiveFade,
    };
  }

  private createBaseTilesByZoom(currentTilesByZoom: TilesByZoomLevel): TilesByZoomLevel {
    const merged: TilesByZoomLevel = new Map();

    const mergeInto = (map?: TilesByZoomLevel) => {
      if (!map) {
        return;
      }
      for (const [zoom, tiles] of map.entries()) {
        const existing = merged.get(zoom) || {};
        merged.set(zoom, { ...existing, ...tiles });
      }
    };

    if (this.shouldRenderBackingTiles()) {
      for (const backingPvs of this.backingPvsByZoom) {
        mergeInto(backingPvs.get?.());
      }
    }

    if (this.shouldRenderFallbackTiles() && this.fallbackPvs && this.fallbackPvs !== this.lastPvs) {
      mergeInto(this.fallbackPvs.get?.());
    }

    return merged;
  }

  private getOverlayTileOpacityByKey(
    currentTilesByZoom: TilesByZoomLevel,
    viewport: ReturnType<BinaryColormapLayerImpl["getTileViewport"]>,
  ) {
    const opacityByKey = new Map<string, number>();
    const baseTilesByZoom = this.createBaseTilesByZoom(currentTilesByZoom);
    const baseCoverageTiles = this.collectPositionedTiles(baseTilesByZoom);
    const disableTransitions = this.shouldDisableTransitions();
    const now = Date.now();
    const fadeKeyPrefix = this.getTimeKey(
      roundToNearestCacheableTime(this.scene.getDisplayTimeWithOffset(), this.props.kind as LayerKindName),
    );
    let hasActiveFade = false;

    for (const [zoom, tiles] of currentTilesByZoom.entries()) {
      for (const [tileKey, positionedTile] of Object.entries(tiles as Record<string, any>)) {
        const opacity = disableTransitions
          ? 1
          : this.getIdealTileOpacity(`${fadeKeyPrefix}:${tileKey}`, positionedTile, baseCoverageTiles, now, viewport);
        opacityByKey.set(tileKey, opacity);
        if (opacity < 1) {
          hasActiveFade = true;
        }
      }
    }

    return { opacityByKey, hasActiveFade };
  }

  private collectPositionedTiles(tilesByZoom: TilesByZoomLevel): Array<{ tileKey: string; positionedTile: any }> {
    const positionedTiles: Array<{ tileKey: string; positionedTile: any }> = [];
    for (const [, tiles] of tilesByZoom.entries()) {
      for (const [tileKey, positionedTile] of Object.entries(tiles as Record<string, any>)) {
        positionedTiles.push({ tileKey, positionedTile });
      }
    }
    return positionedTiles;
  }

  private getDrapingTileSet(
    baseRequest: AreaRequest<{ model: string; parameter_unit: string; ens_select: string; init_date?: string }>,
    primaryArea: AreaRequest<{ model: string; parameter_unit: string; ens_select: string; init_date?: string }>["area"],
  ): { primary?: PvsLike; fallback?: PvsLike } {
    if (!this.tileService) {
      return {};
    }
    const primaryRequest: AreaRequest<{
      model: string;
      parameter_unit: string;
      ens_select: string;
      init_date?: string;
    }> = {
      ...baseRequest,
      area: primaryArea,
    };
    const fallbackArea = this.getParentArea(primaryArea);
    if (!fallbackArea || !this.shouldRequestBackingTiles()) {
      return { primary: this.tileService.getTileSetInstant(primaryRequest) };
    }
    const fallbackRequest: AreaRequest<{
      model: string;
      parameter_unit: string;
      ens_select: string;
      init_date?: string;
    }> = {
      ...baseRequest,
      area: fallbackArea,
    };
    return {
      primary: this.tileService.getTileSetInstant(primaryRequest),
      fallback: this.tileService.getTileSetInstant(fallbackRequest),
    };
  }

  private getParentArea(area: { xyMin: [number, number]; xyMax: [number, number]; z: number; zLodBias: number }) {
    if (area.z <= 0) {
      return undefined;
    }
    return {
      xyMin: [Math.floor(area.xyMin[0] / 2), Math.floor(area.xyMin[1] / 2)] as [number, number],
      xyMax: [Math.floor(area.xyMax[0] / 2), Math.floor(area.xyMax[1] / 2)] as [number, number],
      z: area.z - 1,
      zLodBias: area.zLodBias,
    };
  }

  private getBackingTileSets(
    baseRequest: AreaRequest<{ model: string; parameter_unit: string; ens_select: string; init_date?: string }>,
    currentZoom: number,
  ): PvsLike[] {
    if (!this.tileService || !this.shouldRequestBackingTiles()) {
      return [];
    }

    const backingMaxZoom = Math.min(Math.floor(currentZoom), BACKING_TILE_MAX_ZOOM);
    const backingPvs: PvsLike[] = [];
    for (let zoom = 0; zoom <= backingMaxZoom; zoom++) {
      const backingViewport = this.scene.getMapViewport(Math.max(0, currentZoom - zoom));
      backingPvs.push(
        this.tileService.getTileSetInstant({
          ...baseRequest,
          area: backingViewport.viewportArea,
        }),
      );
    }

    return backingPvs;
  }

  private syncInteractionState(
    dateTimeWithOffset: DateTime,
    viewport: ReturnType<BinaryColormapLayerImpl["getTileViewport"]>,
  ) {
    const timeKey = this.getTimeKey(dateTimeWithOffset);
    const viewKey = this.getViewKey(viewport);
    const currentZoom = viewport.zoom;
    const runtime = getCurrentTileLayerTransitionRuntime();
    const transitionState = {
      lastObservedTimeKey: this.lastObservedTimeKey,
      lastObservedViewKey: this.lastObservedViewKey,
      lastObservedZoomValue: this.lastObservedZoomValue,
      pendingTimeTransition: this.pendingTimeTransition,
      pendingZoomDirection: this.pendingZoomDirection,
    };

    const transitionUpdate = syncTileLayerTransitionState(transitionState, {
      timeKey,
      viewKey,
      zoom: currentZoom,
      runtime,
    });

    if (!shouldRenderRetainedFallbackTiles(runtime)) {
      this.fallbackPvs = undefined;
    }

    if (transitionUpdate.startedTimeTransition) {
      this.idealTileFadeStartMs.clear();
      this.settledIdealTileKeys.clear();
    }

    this.lastObservedTimeKey = transitionState.lastObservedTimeKey;
    this.lastObservedViewKey = transitionState.lastObservedViewKey;
    this.lastObservedZoomValue = transitionState.lastObservedZoomValue;
    this.pendingTimeTransition = transitionState.pendingTimeTransition;
    this.pendingZoomDirection = transitionState.pendingZoomDirection;
  }

  private shouldDisableTransitions() {
    return shouldDisableTileLayerTransitions(getCurrentTileLayerTransitionRuntime());
  }

  private shouldRequestBackingTiles() {
    return shouldRequestLowQualityBackingTiles(getCurrentTileLayerTransitionRuntime(), {
      pendingTimeTransition: this.pendingTimeTransition,
      pendingZoomDirection: this.pendingZoomDirection,
    });
  }

  private shouldRenderBackingTiles() {
    return this.shouldRequestBackingTiles();
  }

  private shouldRenderFallbackTiles() {
    return shouldRenderRetainedFallbackTiles(getCurrentTileLayerTransitionRuntime());
  }

  private captureCurrentAsFallbackIfReady() {
    if (this.pendingTimeTransition || this.pendingZoomDirection || !this.shouldRenderFallbackTiles()) {
      return;
    }
    if (this.lastPvs?.isCovering() && this.lastPvs.props?.isExact) {
      this.fallbackPvs = this.lastPvs;
    }
  }

  private finalizePendingTimeTransition(hasActiveFade: boolean) {
    if (!this.pendingTimeTransition) {
      return;
    }
    if (!this.lastPvs?.isCovering()) {
      return;
    }
    if (hasActiveFade) {
      return;
    }

    this.pendingTimeTransition = false;
    this.captureCurrentAsFallbackIfReady();
  }

  private finalizePendingZoomTransition(hasActiveFade: boolean) {
    if (!this.pendingZoomDirection) {
      return;
    }
    if (!this.lastPvs?.isCovering()) {
      return;
    }
    if (hasActiveFade) {
      return;
    }

    this.pendingZoomDirection = null;
    this.captureCurrentAsFallbackIfReady();
  }

  private captureCurrentAsFallbackAfterFadesSettle(hasActiveFade: boolean) {
    if (hasActiveFade) {
      return;
    }

    this.captureCurrentAsFallbackIfReady();
  }

  private getIdealTileOpacity(
    tileKey: string,
    positionedTile: { tileGeometry?: TileGeometry },
    fallbackCoverageTiles: Array<{ tileKey: string; positionedTile: any }>,
    now: number,
    viewport: ReturnType<BinaryColormapLayerImpl["getTileViewport"]>,
  ) {
    if (!this.hasFallbackCoverage(positionedTile, fallbackCoverageTiles, viewport)) {
      this.idealTileFadeStartMs.delete(tileKey);
      this.settledIdealTileKeys.add(tileKey);
      return 1;
    }

    if (this.settledIdealTileKeys.has(tileKey)) {
      return 1;
    }

    const fadeStartedAt = this.idealTileFadeStartMs.get(tileKey) ?? now;
    if (!this.idealTileFadeStartMs.has(tileKey)) {
      this.idealTileFadeStartMs.set(tileKey, fadeStartedAt);
      this.scene.repaint();
    }

    const progress = Math.max(0, Math.min(1, (now - fadeStartedAt) / TIME_STEP_FADE_MS));
    if (progress >= 1) {
      this.idealTileFadeStartMs.delete(tileKey);
      this.settledIdealTileKeys.add(tileKey);
      return 1;
    }

    this.scene.repaint();
    return progress;
  }

  private hasFallbackCoverage(
    positionedTile: { tileGeometry?: TileGeometry },
    fallbackCoverageTiles: Array<{ tileKey: string; positionedTile: any }>,
    viewport: ReturnType<BinaryColormapLayerImpl["getTileViewport"]>,
  ) {
    const targetGeometry = positionedTile.tileGeometry;
    if (!targetGeometry) {
      return false;
    }

    const targetZoom = Math.floor(viewport.zoom);
    const targetVirtualX = targetGeometry.x * 2 ** (targetZoom - targetGeometry.z);
    const targetVirtualY = targetGeometry.y * 2 ** (targetZoom - targetGeometry.z);
    const targetSpan = 2 ** Math.max(0, targetZoom - targetGeometry.z);

    return fallbackCoverageTiles.some(({ positionedTile: fallbackTile }) => {
      const fallbackGeometry = fallbackTile?.tileGeometry as TileGeometry | undefined;
      if (!fallbackGeometry) {
        return false;
      }

      const fallbackVirtualX = fallbackGeometry.x * 2 ** (targetZoom - fallbackGeometry.z);
      const fallbackVirtualY = fallbackGeometry.y * 2 ** (targetZoom - fallbackGeometry.z);
      const fallbackSpan = 2 ** Math.max(0, targetZoom - fallbackGeometry.z);

      return (
        fallbackVirtualX <= targetVirtualX &&
        fallbackVirtualY <= targetVirtualY &&
        fallbackVirtualX + fallbackSpan >= targetVirtualX + targetSpan &&
        fallbackVirtualY + fallbackSpan >= targetVirtualY + targetSpan
      );
    });
  }

  private getTimeKey(dateTimeWithOffset: DateTime) {
    return dateTimeWithOffset.toUTC().toISO({ suppressMilliseconds: true }) ?? "";
  }

  private getViewKey(viewport: ReturnType<BinaryColormapLayerImpl["getTileViewport"]>) {
    const area = viewport.viewportArea;
    return `${viewport.zoom}|${area.xyMin[0]}|${area.xyMin[1]}|${area.xyMax[0]}|${area.xyMax[1]}|${area.z}`;
  }

  private updateProjectionState(projection: ProjectionSpecification | undefined) {
    if (projection?.name) {
      this.projectionName = projection.name;
    }
    const nextDraping = projection?.name === "globe";
    if (nextDraping && !this.useDraping) {
      this.drapeDirty = true;
    }
    this.useDraping = nextDraping;
  }

  renderToTile(gl: WebGL2RenderingContext, coords: { x: number; y: number; z: number }): void {
    if (!this.useDraping) {
      const projectionName = this.scene.getMapboxMap().getProjection()?.name;
      this.projectionName = projectionName;
      this.useDraping = projectionName === "globe";
      if (!this.useDraping) {
        return;
      }
    }
    if (!this.useDraping || !this.shouldRenderBinary() || !this.props.show || this.props.opacity <= 0.0) {
      return;
    }
    if (!this.tileService) {
      return;
    }

    const tilesByZoomLevel = this.getRenderableTilesByZoomLevel();
    const emptyTile = this.tileService?.networkCache?.emptyTile;
    const failureTile = this.tileService?.networkCache?.failureTile;
    const colormapInfo = this.colormapState.getRenderInfo();

    this.drapeStats.seen = true;

    // Composite 4 child tiles at z+1 to match Mercator resolution. Each quadrant looks up its
    // child tile first; if not yet loaded it falls back through lookupTileOrParent to the z-level
    // parent tile, showing the correct spatial sub-region at lower resolution while loading.
    for (let dy = 0; dy < 2; dy++) {
      for (let dx = 0; dx < 2; dx++) {
        const childCoords = { x: coords.x * 2 + dx, y: coords.y * 2 + dy, z: coords.z + 1 };
        const resolved = this.lookupTileOrParent(tilesByZoomLevel, childCoords);
        if (!resolved) {
          this.drapeStats.missing += 1;
          this.logMissingDrapedTile(childCoords, tilesByZoomLevel);
          continue;
        }

        const { tileData, texCoords, usedParent } = resolved;
        if (tileData === emptyTile || tileData === failureTile) {
          this.drapeStats.missing += 1;
          this.logMissingDrapedTile(childCoords, tilesByZoomLevel, "empty_or_failure");
          continue;
        }

        this.drapeStats.tiles += 1;
        if (usedParent) {
          this.drapeStats.parent += 1;
        }

        this.drapedRenderer.draw(gl, {
          tileTexture: tileData.texture(gl),
          texCoords,
          posCoords: {
            posLeft: dx === 0 ? -1 : 0,
            posRight: dx === 0 ? 0 : 1,
            // In Mapbox renderToTile NDC, y=-1 is the NORTH side of the tile (texTop maps
            // to y=-1 vertices). dy=0 = north child → y ∈ [-1, 0]; dy=1 = south → y ∈ [0, 1].
            posTop: dy === 0 ? 0 : 1,
            posBottom: dy === 0 ? -1 : 0,
          },
          opacity: this.props.opacity,
          colorMapName: this.props.color_map,
          parameterUnit: this.props.parameter_unit,
          geoMinMax: this.geoMinMax,
          colormapInfo,
          debugLoggingEnabled: this.debugLoggingEnabled,
        });
        this.seenRenderToTileThisFrame = true;
        if (this.lastPvs?.isCovering() && !this.colormapState.isPending()) {
          this.drapeDirty = false;
        }
      }
    }
  }

  shouldRerenderTiles(): boolean {
    const projectionName = this.scene.getMapboxMap().getProjection()?.name;
    if (projectionName === "globe" && !this.useDraping) {
      this.projectionName = projectionName;
      this.useDraping = true;
      this.drapeDirty = true;
    } else if (projectionName && projectionName !== "globe" && this.useDraping) {
      this.useDraping = false;
    }
    const should = this.useDraping && this.drapeDirty;
    if (this.debugLoggingEnabled) {
      const now = performance.now();
      if (this.lastShouldRerenderTiles !== should || now - this.lastRerenderLogAt > 2000) {
        this.lastShouldRerenderTiles = should;
        this.lastRerenderLogAt = now;
        // eslint-disable-next-line no-console
        console.debug(`[BinaryDraped] shouldRerenderTiles=${String(should)} useDraping=${String(this.useDraping)}`);
      }
    }
    return should;
  }

  private lookupTile(
    tilesByZoomLevel: TilesByZoomLevel,
    coords: { x: number; y: number; z: number },
  ): { tileData: BinaryTile; tileGeometry: TileGeometry; isFailedTile?: boolean } | undefined {
    const sublayer = tilesByZoomLevel.get(coords.z);
    if (!sublayer) {
      return undefined;
    }
    const geom = new TileGeometry([coords.x, coords.y, coords.z]).wrapToWorldZero();
    const key = geom.toId();
    return sublayer[key] as any;
  }

  private lookupTileOrParent(
    tilesByZoomLevel: TilesByZoomLevel,
    coords: { x: number; y: number; z: number },
  ):
    | {
        tileData: BinaryTile;
        texCoords: { texLeft: number; texRight: number; texTop: number; texBottom: number };
        usedParent: boolean;
      }
    | undefined {
    const direct = this.lookupTile(tilesByZoomLevel, coords);
    if (direct?.tileData && !direct.isFailedTile) {
      return { tileData: direct.tileData, texCoords: FULL_TEXTURE_COORDS, usedParent: false };
    }

    // Fallback to parent tiles (scale up) if current tile is missing.
    for (let dz = 1; dz <= coords.z; dz++) {
      const parentZ = coords.z - dz;
      if (parentZ < 0) {
        break;
      }
      const scale = 1 << dz;
      const parentX = Math.floor(coords.x / scale);
      const parentY = Math.floor(coords.y / scale);
      const parent = this.lookupTile(tilesByZoomLevel, { x: parentX, y: parentY, z: parentZ });
      if (parent?.tileData && !parent.isFailedTile) {
        const dx = coords.x - parentX * scale;
        const dy = coords.y - parentY * scale;
        const texCoords = calculateTextureCoordinates(dx, dy, scale);
        return { tileData: parent.tileData, texCoords, usedParent: true };
      }
    }

    return undefined;
  }

  private logMissingDrapedTile(
    coords: { x: number; y: number; z: number },
    tilesByZoomLevel: TilesByZoomLevel,
    reason = "not_found",
  ): void {
    if (!this.debugLoggingEnabled) {
      return;
    }
    const now = performance.now();
    if (now - this.lastMissingLogAt < 2000) {
      return;
    }
    this.lastMissingLogAt = now;
    const zooms = Array.from(tilesByZoomLevel.keys()).sort((a, b) => a - b);
    const zoomSizes = zooms.map((z) => `${z}:${Object.keys(tilesByZoomLevel.get(z) || {}).length}`).join(",");
    const pvsCovering = this.lastPvs?.isCovering() ?? false;
    const viewport = this.getTileViewport();
    // eslint-disable-next-line no-console
    console.debug(
      `[BinaryDraped] missing tile reason=${reason} coords=${coords.x}/${coords.y}/${coords.z} ` +
        `pvsCovering=${String(pvsCovering)} zoom=${viewport.zoom.toFixed(2)} ` +
        `zoomWithPrecision=${viewport.zoomWithPrecision.toFixed(4)} ` +
        `tilesByZoom={${zoomSizes}}`,
    );
  }

  private getTileViewport() {
    return this.scene.getMapViewport();
  }

  beforeRender(): void {
    this.syncDebugTileGrid();
  }

  fetchData(timeFrame: DateTime): VoidAsyncResult {
    if (!this.tileService) {
      return createResolvedAsyncResult(undefined);
    }

    const viewport = this.getTileViewport();
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(timeFrame),
      this.props.kind as LayerKindName,
    );

    const customInitDate =
      typeof this.props.custom_options?.init_date === "string" ? this.props.custom_options.init_date.trim() : "";
    const customOptions = this.props.custom_options as { api_url_suffix?: string } | undefined;
    const customApiUrlSuffix =
      typeof customOptions?.api_url_suffix === "string" ? customOptions.api_url_suffix.trim() : "";
    const areaRequest: AreaRequest<{
      model: string;
      parameter_unit: string;
      ens_select: string;
      init_date?: string;
      api_url_suffix?: string;
    }> = {
      area: viewport.viewportArea,
      datetime: dateTimeWithOffset,
      requestParams: {
        model: this.props.model,
        parameter_unit: this.props.parameter_unit,
        ens_select: this.props.ens_select || "",
        init_date: customInitDate || undefined,
        api_url_suffix: customApiUrlSuffix || undefined,
      },
    };

    const results: VoidAsyncResult[] = [];
    const pvs = this.tileService.getTileSetInstant(areaRequest);
    results.push(pvs.toVoidAsyncResult());
    if (this.useDraping) {
      const drape = this.getDrapingTileSet(areaRequest, viewport.viewportArea);
      if (drape.primary?.toVoidAsyncResult) {
        results.push(drape.primary.toVoidAsyncResult());
      }
      if (drape.fallback?.toVoidAsyncResult) {
        results.push(drape.fallback.toVoidAsyncResult());
      }
    }
    return combineVoidAsyncResults(results);
  }

  isRebuffering(timeFrame: DateTime): boolean {
    if (!this.tileService) {
      return false;
    }

    const viewport = this.getTileViewport();
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(timeFrame),
      this.props.kind as LayerKindName,
    );
    const customInitDate =
      typeof this.props.custom_options?.init_date === "string" ? this.props.custom_options.init_date.trim() : "";
    const customOptions = this.props.custom_options as { api_url_suffix?: string } | undefined;
    const customApiUrlSuffix =
      typeof customOptions?.api_url_suffix === "string" ? customOptions.api_url_suffix.trim() : "";
    const areaRequest: AreaRequest<{
      model: string;
      parameter_unit: string;
      ens_select: string;
      init_date?: string;
      api_url_suffix?: string;
    }> = {
      area: viewport.viewportArea,
      datetime: dateTimeWithOffset,
      requestParams: {
        model: this.props.model,
        parameter_unit: this.props.parameter_unit,
        ens_select: this.props.ens_select || "",
        init_date: customInitDate || undefined,
        api_url_suffix: customApiUrlSuffix || undefined,
      },
    };

    const pvs = this.tileService.getTileSetInstant(areaRequest);
    let missingTiles = !pvs.isCovering();

    if (this.useDraping) {
      const drape = this.getDrapingTileSet(areaRequest, viewport.viewportArea);
      if (drape.primary && !drape.primary.isCovering()) {
        missingTiles = true;
      }
      if (drape.fallback && !drape.fallback.isCovering()) {
        missingTiles = true;
      }
    }

    return missingTiles || this.colormapState.isPending();
  }

  getActiveWeatherParametersAsString() {
    return [
      {
        model: this.props.model,
        parameter: this.props.parameter_unit,
        ensSelect: this.props.ens_select,
      },
    ];
  }

  removeLayer(): void {
    const map = this.scene.getMapboxMap();
    this.removeDebugTileGridLayer();
    if (map.getLayer(this.id)) {
      map.removeLayer(this.id);
    }
    this.tileService?.clear();
    this.tileService = undefined;
    this.lastPvs = undefined;
    this.fallbackPvs = undefined;
    this.backingPvsByZoom = [];
    this.drapePvs = undefined;
    this.drapePvsFallback = undefined;
    if (this.gl) {
      this.debugRenderer.dispose(this.gl);
      this.prepassRenderer.dispose(this.gl);
      this.mainpassRenderer.dispose(this.gl);
      this.drapedRenderer.dispose(this.gl);
      this.colormapState.dispose(this.gl);
      this.gl = undefined;
    }
  }

  private syncDebugTileGrid(): void {
    const map = this.scene.getMapboxMap();
    const viewport = this.getTileViewport();
    const area = viewport.viewportArea;
    const debugTiles = this.getDebugTilesForArea(area.xyMin[0], area.xyMax[0], area.xyMin[1], area.xyMax[1], area.z);

    if (this.useDraping && area.z > 0) {
      const parentTiles = this.getDebugTilesForArea(
        Math.floor(area.xyMin[0] / 2),
        Math.floor(area.xyMax[0] / 2),
        Math.floor(area.xyMin[1] / 2),
        Math.floor(area.xyMax[1] / 2),
        area.z - 1,
      );
      debugTiles.push(...parentTiles);
    }

    this.requestedTileGridOverlay.sync(
      map,
      debugTiles,
      this.props.show && this.props.opacity > 0 && isWmtsTileBoundariesDebugEnabled(),
    );
  }

  private getDebugTilesForArea(minX: number, maxX: number, minY: number, maxY: number, z: number): WmtsDebugTile[] {
    const maxCoord = 2 ** z - 1;
    const tiles: WmtsDebugTile[] = [];

    for (let x = minX; x <= maxX; x++) {
      if (x < 0 || x > maxCoord) {
        continue;
      }
      for (let y = minY; y <= maxY; y++) {
        if (y < 0 || y > maxCoord) {
          continue;
        }
        tiles.push({ x, y, z });
      }
    }

    return tiles;
  }

  private removeDebugTileGridLayer(): void {
    const map = this.scene.getMapboxMap();
    this.requestedTileGridOverlay.remove(map);
  }
}
